import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { useLanguage } from '../hooks/useLanguage';
import { LANGS } from '../utils/i18n';
import Sim360Neon from './Sim360Neon';

// Logo configuration - use environment variables
const LOGO_SRC = process.env.REACT_APP_LOGO_URL;
const LOGO_ALT = "DCA Club Logo";
const LOGO_FALLBACK = process.env.REACT_APP_LOGO_URL_FALLBACK || "DCA Club";

const TopBar = () => {
  const { currentLang, switchLang, t } = useLanguage();
  const [logoError, setLogoError] = useState(false);

  const handleLanguageToggle = (lang) => {
    if (lang !== currentLang) {
      switchLang(lang);
    }
  };

  const handleLogoClick = () => {
    let homeUrl;
    if (currentLang === 'en') {
      homeUrl = 'https://thedcaclub.com/en';
    } else if (currentLang === 'es') {
      homeUrl = 'https://thedcaclub.com/es';
    } else if (currentLang === 'fr') {
      homeUrl = 'https://thedcaclub.com/fr';
    } else {
      homeUrl = 'https://thedcaclub.com/en'; // Default fallback
    }
    window.location.href = homeUrl;
  };

  return (
    <header 
      className="sticky top-0 z-[1000] h-16 px-4 lg:px-6 flex items-center justify-between border-b border-[#0b1c38]"
      style={{
        background: 'rgba(4,8,22,0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)' // Safari support
      }}
    >
      {/* Logo + SIM360 Neon Section */}
      <div className="flex items-center gap-3 lg:gap-4">
        <button
          onClick={handleLogoClick}
          className="flex items-center hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#00f4ff] focus:ring-offset-2 focus:ring-offset-transparent rounded"
          aria-label="Go to home"
          data-testid="topbar-logo"
        >
          {!logoError ? (
            <img
              src={LOGO_SRC}
              alt={LOGO_ALT}
              className="h-7 lg:h-8 w-auto"
              onError={() => setLogoError(true)}
              loading="eager"
            />
          ) : (
            <span className="text-[#00f4ff] font-bold text-lg lg:text-xl">
              {LOGO_FALLBACK}
            </span>
          )}
        </button>
        <span className="hidden sm:inline-block w-px h-6 bg-[#0b1c38]" aria-hidden />
        <Sim360Neon size="md" className="hidden sm:inline-flex" />
      </div>

      {/* Right Section: Language Toggle + Backoffice */}
      <div className="flex items-center gap-4 lg:gap-6">
        {/* Language Toggle */}
        <div 
          className="flex items-center gap-1"
          role="radiogroup"
          aria-label={t('changeLanguage')}
        >
          <button
            onClick={() => handleLanguageToggle(LANGS.EN)}
            className={`px-2 py-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#00f4ff] focus:ring-offset-2 focus:ring-offset-transparent rounded ${
              currentLang === LANGS.EN 
                ? 'text-[#00f4ff]' 
                : 'text-[#a8c2d8] hover:text-[#00f4ff]'
            }`}
            role="radio"
            aria-checked={currentLang === LANGS.EN}
            aria-label="English"
          >
            EN
          </button>
          <span className="text-[#a8c2d8] text-sm">|</span>
          <button
            onClick={() => handleLanguageToggle(LANGS.ES)}
            className={`px-2 py-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#00f4ff] focus:ring-offset-2 focus:ring-offset-transparent rounded ${
              currentLang === LANGS.ES 
                ? 'text-[#00f4ff]' 
                : 'text-[#a8c2d8] hover:text-[#00f4ff]'
            }`}
            role="radio"
            aria-checked={currentLang === LANGS.ES}
            aria-label="Español"
          >
            ES
          </button>
          <span className="text-[#a8c2d8] text-sm">|</span>
          <button
            onClick={() => handleLanguageToggle(LANGS.FR)}
            className={`px-2 py-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#00f4ff] focus:ring-offset-2 focus:ring-offset-transparent rounded ${
              currentLang === LANGS.FR 
                ? 'text-[#00f4ff]' 
                : 'text-[#a8c2d8] hover:text-[#00f4ff]'
            }`}
            role="radio"
            aria-checked={currentLang === LANGS.FR}
            aria-label="Français"
          >
            FR
          </button>
        </div>

        {/* Backoffice (external) Button */}
        <Button
          onClick={() => {
            window.location.href = 'https://my.thedcaclub.com';
          }}
          variant="outline"
          className="border-[#7c3aed] text-[#a78bfa] hover:bg-[rgba(124,58,237,0.10)] rounded-full px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium min-h-[44px] transition-all focus:ring-2 focus:ring-[#a78bfa] focus:ring-offset-2 focus:ring-offset-transparent"
          aria-label={t('backofficeExternal')}
          data-testid="topbar-backoffice-btn"
        >
          {t('backofficeExternal')}
        </Button>

        {/* My Portafolio Button */}
        <Button
          onClick={() => {
            window.location.href = '/myportafolio';
          }}
          className="bg-gradient-to-r from-[#0051ff] to-[#00e5ff] text-[#020817] hover:brightness-110 rounded-full px-3 lg:px-4 py-2 text-xs lg:text-sm font-bold min-h-[44px] transition-all focus:ring-2 focus:ring-[#00e5ff] focus:ring-offset-2 focus:ring-offset-transparent shadow-[0_0_18px_-4px_rgba(0,114,255,0.75)]"
          aria-label={t('myPortafolio')}
          data-testid="topbar-myportafolio-btn"
        >
          {t('myPortafolio')}
        </Button>
      </div>
    </header>
  );
};

export default TopBar;